<template>
  <section class="mt-8" v-if="modelValue">
    <h3 class="text-lg">{{ quantityTitle || wt('areaDistribution', 'quantity', 'title') }}</h3>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6 xl:grid-cols-3">
      <div>
        <label class="area-distribution-label">
          {{ quantityLabel || wt('areaDistribution', 'quantity', 'label') }}
        </label>
        <div class="flex items-center">
          <NumberInput
            v-model="quantityArea"
            :formatBigNumber="true"
            :class="{
              'core-form-error': validator.getErrors(quantityAreaKey),
            }"
            class="mb-0" />
          <div class="w-24 ml-3 text-neutral-550">{{ $t('general.sq_m') }}</div>
        </div>
      </div>
    </div>
    <ErrorMessage :name="quantityAreaKey" class="error-message" />
  </section>

  <section class="mt-12" v-if="modelValue">
    <h3 class="mb-4 text-lg">
      {{ distributionTitle || wt('areaDistribution', 'distribution', 'title') }}
    </h3>
    <RatiosInput v-if="usageCount > 1" v-model="usageItems" :allowRemoval="true" :quantityArea="quantityArea" />
    <div v-else-if="usageCount === 1" class="core-split-panes border p-2 area-distribution-hint">
      {{ $t('project.general.sections.usage_definition.usage.hint.distribution_single') }}
    </div>
    <div v-else class="core-split-panes border p-2 area-distribution-hint">
      {{ $t('project.general.sections.usage_definition.usage.hint.init') }}
    </div>
  </section>

  <section v-if="modelValue && showCivilEngineerAreaDistribution && usageCount" class="mt-12">
    <h3 class="mb-4 text-lg">
      {{ getCivilEngineerSectionTitle() }}
    </h3>

    <div>
      <h4 class="mb-2 font-medium text-neutral-700">{{ getCivilEngineerSlabAreaTitle() }}</h4>
      <label class="area-distribution-label">
        {{ getCivilEngineerSlabAreaLabel() }}
      </label>
      <div class="flex items-center">
        <NumberInput
          v-model="civilEngineerSlabArea"
          :formatBigNumber="true"
          :class="{
            'core-form-error': validator.getErrors('civilEngineerSlabArea'),
          }"
          class="mb-0" />
        <div class="w-24 ml-3 text-neutral-550">{{ $t('general.sq_m') }}</div>
      </div>
      <ErrorMessage name="civilEngineerSlabArea" class="error-message" />
      <p v-if="quantityArea > 0 && civilEngineerSlabArea" class="mt-3 text-sm text-neutral-550">
        {{ getCivilEngineerSlabAreaRatioLabel() }}: {{ formatFactor(civilEngineerSlabAreaRatio) }}
      </p>
      <div class="area-distribution-factor-box">
        <span class="font-medium text-neutral-700">
          {{ getCivilEngineerSlabAreaFactorLabel() }}
        </span>
        <span class="text-xl font-bold text-primary-default">
          {{ formatFactor(civilEngineerSlabAreaFactor) }}
        </span>
      </div>
    </div>

    <div class="mt-12">
      <h4 class="mb-4 font-medium text-neutral-700">{{ getCivilEngineerTerrainTitle() }}</h4>
      <div class="border-t border-neutral-300">
        <AccordionItem
          v-for="(usage, index) in civilEngineerUsageItems"
          :key="`${usage.type}-${usage.typeCounter}-${index}`"
          :title="getCivilEngineerUsageTitle(usage, index)"
          :description="getCivilEngineerTerrainAccordionDescription(usage)"
          :result="formatFactor(getCivilEngineerBelowTerrainFactor(usage))"
          :open="activeCivilEngineerTerrainAccordionKey === getCivilEngineerTerrainAccordionKey(index)"
          :controlledOpen="true"
          @toggle="onCivilEngineerTerrainAccordionToggle(index, $event)">
          <RatiosInput
            :modelValue="getCivilEngineerTerrainDistribution(usage)"
            @update:modelValue="setCivilEngineerTerrainDistribution(index, $event)"
            :allowRemoval="false"
            :quantityArea="getUsageArea(usage)"
            :minRatio="0"
            :minArea="0"
            :titleResolver="getCivilEngineerTerrainItemTitle" />
          <div class="area-distribution-factor-box">
            <span class="font-medium text-neutral-700">
              {{ getCivilEngineerTerrainFactorLabel() }}
            </span>
            <span class="text-xl font-bold text-primary-default">
              {{ formatFactor(getCivilEngineerBelowTerrainFactor(usage)) }}
            </span>
          </div>
        </AccordionItem>
      </div>
    </div>
  </section>

  <section v-if="modelValue && showBuiltOverAreaDistribution && usageCount" class="mt-12">
    <h3 class="mb-4 text-lg">
      {{ getBuiltOverAreaTitle() }}
    </h3>
    <div class="border-t border-neutral-300">
      <AccordionItem
        v-for="(usage, index) in landscapeUsageItems"
        :key="`${usage.type}-${usage.typeCounter}-${index}`"
        :title="getLandscapeUsageTitle(usage, index)"
        :description="getBuiltOverAreaAccordionDescription(usage)"
        :result="formatFactor(getBuiltOverAreaFactor(usage))"
        :open="activeBuiltOverAreaAccordionKey === getBuiltOverAreaAccordionKey(index)"
        :controlledOpen="true"
        @toggle="onBuiltOverAreaAccordionToggle(index, $event)">
        <RatiosInput
          :modelValue="getBuiltOverAreaDistribution(usage)"
          @update:modelValue="setBuiltOverAreaDistribution(index, $event)"
          :allowRemoval="false"
          :quantityArea="getUsageArea(usage)"
          :minRatio="0"
          :minArea="0"
          :titleResolver="getBuiltOverAreaItemTitle" />
        <div class="area-distribution-factor-box">
          <span class="font-medium text-neutral-700">
            {{ getBuiltOverAreaFactorLabel() }}
          </span>
          <span class="text-xl font-bold text-primary-default">
            {{ formatFactor(getBuiltOverAreaFactor(usage)) }}
          </span>
        </div>
      </AccordionItem>
    </div>
  </section>
</template>

<script lang="ts" setup generic="T extends { quantityArea: number; usageItems: SharedUsageItem[] }">
import NumberInput from '@/components/partials/inputs/NumberInput.vue'
import RatiosInput from '@/components/partials/inputs/RatiosInput.vue'
import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import { useValidator } from '@/validation/form-validator'
import { computed, ref, type PropType } from 'vue'
import type { SharedUsageItem } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useContentStore } from '@/stores/content'

const { wt } = useWidgetText('widget-texts-project-general')
const contentStore = useContentStore()
const props = withDefaults(
  defineProps<{
    quantityAreaKey?: string
    usageItemsKey?: string
    quantityTitle?: string
    quantityLabel?: string
    distributionTitle?: string
    showBuiltOverAreaDistribution?: boolean
    showCivilEngineerAreaDistribution?: boolean
  }>(),
  {
    quantityAreaKey: 'quantityArea',
    usageItemsKey: 'usageItems',
    quantityTitle: '',
    quantityLabel: '',
    distributionTitle: '',
    showBuiltOverAreaDistribution: false,
    showCivilEngineerAreaDistribution: false,
  }
)
const modelValue = defineModel({ type: Object as PropType<T> })
const emit = defineEmits<{
  civilEngineerSlabAreaChange: [value: number | null]
}>()
const validator = useValidator()
const activeBuiltOverAreaAccordionKey = ref<string | null>(null)
const activeCivilEngineerTerrainAccordionKey = ref<string | null>(null)

function updateModelField(key: string, value: unknown): void {
  if (!modelValue.value) return
  ;(modelValue.value as Record<string, unknown>)[key] = value
  modelValue.value = {
    ...modelValue.value,
    [key]: value,
  }
}

const quantityArea = computed({
  get: () => Number((modelValue.value as Record<string, unknown> | undefined)?.[props.quantityAreaKey] ?? 0),
  set: (value: number) => {
    updateModelField(props.quantityAreaKey, value)
  },
})
const usageItems = computed({
  get: () =>
    ((modelValue.value as Record<string, unknown> | undefined)?.[props.usageItemsKey] ?? []) as SharedUsageItem[],
  set: (value: SharedUsageItem[]) => {
    updateModelField(props.usageItemsKey, value)
  },
})
const usageCount = computed(() => usageItems.value.length)
const civilEngineerSlabArea = computed<number | null>({
  get: () => {
    const value = (modelValue.value as Record<string, unknown> | undefined)?.civilEngineerSlabArea
    return typeof value === 'number' && Number.isFinite(value) ? value : null
  },
  set: (value: number | null) => {
    updateModelField('civilEngineerSlabArea', value)
    emit('civilEngineerSlabAreaChange', value)
  },
})
const civilEngineerSlabAreaRatio = computed(() => {
  if (!civilEngineerSlabArea.value || quantityArea.value <= 0) return 0
  return civilEngineerSlabArea.value / quantityArea.value
})
const civilEngineerSlabAreaFactor = computed(() => getCivilEngineerSlabAreaFactor(civilEngineerSlabAreaRatio.value))

type LandscapeUsageItem = SharedUsageItem & {
  builtOverAreaRatio?: number | null
  unbuiltAreaRatio?: number | null
  buildingTypeId?: string | null
  buildingTypeName?: string | null
}

type CivilEngineerUsageItem = SharedUsageItem & {
  aboveTerrainAreaRatio?: number | null
  belowTerrainAreaRatio?: number | null
  buildingTypeId?: string | null
  buildingTypeName?: string | null
}

type BuiltOverAreaDistributionItem = SharedUsageItem & {
  builtOverAreaType: 'BUILT_OVER' | 'UNBUILT'
}

type CivilEngineerTerrainDistributionItem = SharedUsageItem & {
  civilEngineerTerrainType: 'ABOVE_TERRAIN' | 'BELOW_TERRAIN'
}

const BUILT_OVER_AREA_THRESHOLDS = [
  { percentage: 0, factor: 1 },
  { percentage: 12.5, factor: 1.05 },
  { percentage: 25, factor: 1.1 },
  { percentage: 37.5, factor: 1.15 },
  { percentage: 50, factor: 1.2 },
  { percentage: 62.5, factor: 1.25 },
  { percentage: 75, factor: 1.3 },
  { percentage: 87.5, factor: 1.35 },
  { percentage: 100, factor: 1.4 },
] as const
// SIA 103 total slab area factor: ratio total slab area / floor area maps linearly onto
// the factor (1:1 -> 1.0, 1.5:1 -> 1.5, 2:1 -> 2.0), clamped to that range.
const CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN = 1
const CIVIL_ENGINEER_SLAB_AREA_FACTOR_MAX = 2
const CIVIL_ENGINEER_BELOW_TERRAIN_THRESHOLDS = [
  { percentage: 0, factor: 1.05 },
  { percentage: 12.5, factor: 1.1 },
  { percentage: 25, factor: 1.15 },
  { percentage: 37.5, factor: 1.2 },
  { percentage: 50, factor: 1.25 },
  { percentage: 62.5, factor: 1.3 },
  { percentage: 75, factor: 1.35 },
  { percentage: 87.5, factor: 1.4 },
] as const

const landscapeUsageItems = computed(() => usageItems.value as LandscapeUsageItem[])
const civilEngineerUsageItems = computed(() => usageItems.value as CivilEngineerUsageItem[])

function getBuiltOverAreaAccordionKey(index: number): string {
  return `${props.usageItemsKey}:builtOverArea:${index}`
}

function onBuiltOverAreaAccordionToggle(index: number, open: boolean): void {
  activeBuiltOverAreaAccordionKey.value = open ? getBuiltOverAreaAccordionKey(index) : null
}

function getCivilEngineerTerrainAccordionKey(index: number): string {
  return `${props.usageItemsKey}:civilEngineerTerrain:${index}`
}

function onCivilEngineerTerrainAccordionToggle(index: number, open: boolean): void {
  activeCivilEngineerTerrainAccordionKey.value = open ? getCivilEngineerTerrainAccordionKey(index) : null
}

function getBuiltOverAreaDistribution(usage: LandscapeUsageItem): BuiltOverAreaDistributionItem[] {
  const builtOverAreaRatio = clampPercentage(usage.builtOverAreaRatio ?? 0)
  const unbuiltAreaRatio = clampPercentage(100 - builtOverAreaRatio)
  return [
    createBuiltOverAreaDistributionItem(usage, 'BUILT_OVER', builtOverAreaRatio),
    createBuiltOverAreaDistributionItem(usage, 'UNBUILT', unbuiltAreaRatio),
  ]
}

function setBuiltOverAreaDistribution(index: number, value: SharedUsageItem[] | undefined): void {
  if (!modelValue.value) return
  const distribution = (value ?? []) as BuiltOverAreaDistributionItem[]
  const builtOverAreaRatio = clampPercentage(
    distribution.find((item) => item.builtOverAreaType === 'BUILT_OVER')?.ratio ?? 0
  )
  const unbuiltAreaRatio = clampPercentage(
    distribution.find((item) => item.builtOverAreaType === 'UNBUILT')?.ratio ?? 100 - builtOverAreaRatio
  )

  modelValue.value = {
    ...modelValue.value,
    [props.usageItemsKey]: landscapeUsageItems.value.map((usage, usageIndex) =>
      usageIndex === index
        ? {
            ...usage,
            builtOverAreaRatio,
            unbuiltAreaRatio,
          }
        : usage
    ),
  }
}

function getCivilEngineerTerrainDistribution(usage: CivilEngineerUsageItem): CivilEngineerTerrainDistributionItem[] {
  const belowTerrainAreaRatio = clampPercentage(usage.belowTerrainAreaRatio ?? 0)
  const aboveTerrainAreaRatio = clampPercentage(100 - belowTerrainAreaRatio)
  return [
    createCivilEngineerTerrainDistributionItem(usage, 'ABOVE_TERRAIN', aboveTerrainAreaRatio),
    createCivilEngineerTerrainDistributionItem(usage, 'BELOW_TERRAIN', belowTerrainAreaRatio),
  ]
}

function setCivilEngineerTerrainDistribution(index: number, value: SharedUsageItem[] | undefined): void {
  if (!modelValue.value) return
  const distribution = (value ?? []) as CivilEngineerTerrainDistributionItem[]
  const belowTerrainAreaRatio = clampPercentage(
    distribution.find((item) => item.civilEngineerTerrainType === 'BELOW_TERRAIN')?.ratio ?? 0
  )
  const aboveTerrainAreaRatio = clampPercentage(100 - belowTerrainAreaRatio)

  modelValue.value = {
    ...modelValue.value,
    [props.usageItemsKey]: civilEngineerUsageItems.value.map((usage, usageIndex) =>
      usageIndex === index
        ? {
            ...usage,
            aboveTerrainAreaRatio,
            belowTerrainAreaRatio,
          }
        : usage
    ),
  }
}

function createBuiltOverAreaDistributionItem(
  usage: LandscapeUsageItem,
  builtOverAreaType: BuiltOverAreaDistributionItem['builtOverAreaType'],
  ratio: number
): BuiltOverAreaDistributionItem {
  return {
    ...usage,
    typeCounter: builtOverAreaType === 'BUILT_OVER' ? 0 : 1,
    ratio,
    builtOverAreaType,
  }
}

function createCivilEngineerTerrainDistributionItem(
  usage: CivilEngineerUsageItem,
  civilEngineerTerrainType: CivilEngineerTerrainDistributionItem['civilEngineerTerrainType'],
  ratio: number
): CivilEngineerTerrainDistributionItem {
  return {
    ...usage,
    typeCounter: civilEngineerTerrainType === 'ABOVE_TERRAIN' ? 0 : 1,
    ratio,
    civilEngineerTerrainType,
  }
}

function clampPercentage(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(100, Math.max(0, value))
}

function getUsageArea(usage: LandscapeUsageItem): number {
  return (quantityArea.value * usage.ratio) / 100
}

function getLandscapeUsageTitle(usage: LandscapeUsageItem, index: number): string {
  return getUsageDisplayTitle(usage, wt, {
    allUsages: landscapeUsageItems.value,
    index,
    resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
  })
}

function getCivilEngineerUsageTitle(usage: CivilEngineerUsageItem, index: number): string {
  return getUsageDisplayTitle(usage, wt, {
    allUsages: civilEngineerUsageItems.value,
    index,
    resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
  })
}

function getBuiltOverAreaItemTitle(item: SharedUsageItem): string {
  const builtOverAreaType = (item as BuiltOverAreaDistributionItem).builtOverAreaType
  return (
    wt('areaDistribution', 'landscape', 'builtOverArea', 'option', builtOverAreaType) ||
    (builtOverAreaType === 'BUILT_OVER' ? 'Anteil unterbauter Fläche' : 'Anteil nicht unterbauter Fläche')
  )
}

function getCivilEngineerTerrainItemTitle(item: SharedUsageItem): string {
  const civilEngineerTerrainType = (item as CivilEngineerTerrainDistributionItem).civilEngineerTerrainType
  return (
    wt('areaDistribution', 'civilEngineer', 'terrain', 'option', civilEngineerTerrainType) ||
    (civilEngineerTerrainType === 'ABOVE_TERRAIN'
      ? 'Fläche über massgebendem Terrain'
      : 'Fläche unter massgebendem Terrain')
  )
}

function getCivilEngineerSectionTitle(): string {
  return wt('areaDistribution', 'civilEngineer', 'title') || 'SIA 103 - Bauingenieur*innen'
}

function getCivilEngineerSlabAreaTitle(): string {
  return wt('areaDistribution', 'civilEngineer', 'slabArea', 'title') || 'Gesamte Plattenfläche'
}

function getCivilEngineerSlabAreaLabel(): string {
  return wt('areaDistribution', 'civilEngineer', 'slabArea', 'label') || 'Gesamte Plattenfläche in m2'
}

function getCivilEngineerSlabAreaRatioLabel(): string {
  return wt('areaDistribution', 'civilEngineer', 'slabArea', 'ratio') || 'Verhältnis Gesamte Plattenfläche / Geschossfläche'
}

function getCivilEngineerSlabAreaFactorLabel(): string {
  return wt('areaDistribution', 'civilEngineer', 'slabArea', 'factor') || 'Faktor Gesamte Plattenfläche'
}

function getCivilEngineerTerrainTitle(): string {
  return wt('areaDistribution', 'civilEngineer', 'terrain', 'title') || 'Fläche über / unter Terrain'
}

function getCivilEngineerTerrainFactorLabel(): string {
  return wt('areaDistribution', 'civilEngineer', 'terrain', 'factor') || 'Faktor Fläche unter massgebendem Terrain'
}

function getBuiltOverAreaTitle(): string {
  return wt('areaDistribution', 'landscape', 'builtOverArea', 'title') || 'Anteil unterbauter Fläche'
}

function getBuiltOverAreaFactorLabel(): string {
  return wt('areaDistribution', 'landscape', 'builtOverArea', 'factor') || 'Faktor für unterbaute Flächen'
}

function getBuiltOverAreaAccordionDescription(usage: LandscapeUsageItem): string {
  const builtOverAreaRatio = Math.round(clampPercentage(usage.builtOverAreaRatio ?? 0))
  const unbuiltAreaRatio = Math.round(100 - builtOverAreaRatio)
  return `${getBuiltOverAreaItemTitle(createBuiltOverAreaDistributionItem(usage, 'BUILT_OVER', builtOverAreaRatio))}: ${builtOverAreaRatio}%&nbsp;&nbsp;·&nbsp;&nbsp;${getBuiltOverAreaItemTitle(createBuiltOverAreaDistributionItem(usage, 'UNBUILT', unbuiltAreaRatio))}: ${unbuiltAreaRatio}%`
}

function getCivilEngineerTerrainAccordionDescription(usage: CivilEngineerUsageItem): string {
  const belowTerrainAreaRatio = Math.round(clampPercentage(usage.belowTerrainAreaRatio ?? 0))
  const aboveTerrainAreaRatio = Math.round(100 - belowTerrainAreaRatio)
  return `${getCivilEngineerTerrainItemTitle(createCivilEngineerTerrainDistributionItem(usage, 'ABOVE_TERRAIN', aboveTerrainAreaRatio))}: ${aboveTerrainAreaRatio}%&nbsp;&nbsp;·&nbsp;&nbsp;${getCivilEngineerTerrainItemTitle(createCivilEngineerTerrainDistributionItem(usage, 'BELOW_TERRAIN', belowTerrainAreaRatio))}: ${belowTerrainAreaRatio}%`
}

function getBuiltOverAreaFactor(usage: LandscapeUsageItem): number {
  const builtOverPercentage = clampPercentage(usage.builtOverAreaRatio ?? 0)
  return BUILT_OVER_AREA_THRESHOLDS.reduce((factor, threshold) => {
    return builtOverPercentage >= threshold.percentage ? threshold.factor : factor
  }, 1)
}

function getCivilEngineerSlabAreaFactor(slabAreaRatio: number): number {
  if (!Number.isFinite(slabAreaRatio) || slabAreaRatio <= 0) return CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN
  return Math.min(CIVIL_ENGINEER_SLAB_AREA_FACTOR_MAX, Math.max(CIVIL_ENGINEER_SLAB_AREA_FACTOR_MIN, slabAreaRatio))
}

function getCivilEngineerBelowTerrainFactor(usage: CivilEngineerUsageItem): number {
  const belowTerrainPercentage = clampPercentage(usage.belowTerrainAreaRatio ?? 0)
  return CIVIL_ENGINEER_BELOW_TERRAIN_THRESHOLDS.reduce((factor, threshold) => {
    return belowTerrainPercentage > threshold.percentage ? threshold.factor : factor
  }, 1)
}

function formatFactor(value: number): string {
  return value.toFixed(2)
}
</script>

<style lang="postcss" scoped>
.area-distribution-label {
  @apply inline-block w-full pt-2 pb-1 text-neutral-550;
}

.area-distribution-hint {
  @apply text-neutral-550;
}

.area-distribution-factor-box {
  @apply mt-4 flex items-center justify-between border border-neutral-300 bg-neutral-35 p-5;
}
</style>
